import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import type {
  FormularyLookupResponse,
  GetMedicationCoverageResponse,
} from "../types";

export type {
  FormularyCopay,
  FormularyLookupResponse,
  PayerAlternative,
  TherapeuticAlternative,
} from "../types";

interface FormularyLookupParams {
  patientId: string;
  patientEligibilityId: number;
  ndc: string;
  enabled: boolean;
}

export const useFormularyLookup = ({
  patientId,
  patientEligibilityId,
  ndc,
  enabled,
}: FormularyLookupParams) =>
  useApi<FormularyLookupResponse>({
    queryOptions: {
      enabled: Boolean(patientId && patientEligibilityId && ndc && enabled),
      staleTime: 0,
      queryFn: async () => {
        const response = await axiosInstance.get<GetMedicationCoverageResponse>(
          "/rx/eligibility/formulary",
          {
            params: {
              patient_id: patientId,
              patient_eligibility_id: patientEligibilityId,
              ndc,
            },
          },
        );
        if (response.data.status === "QUEUED") {
          throw new Error("QUEUED");
        }
        if (!response.data.coverage) {
          throw new Error("NO_COVERAGE");
        }
        return response.data.coverage;
      },
      queryKey: QueryKeys.Patient.Chart.FormularyLookup.DEFAULT(
        patientId,
        patientEligibilityId,
        ndc,
      ),
      retry: (failureCount, error) => {
        if (
          error instanceof Error &&
          (error.message === "QUEUED" || error.message === "NO_COVERAGE")
        ) {
          return failureCount < 5;
        }
        return false;
      },
      retryDelay: 2000,
    },
  });
